/**
 * 새소식 (?page=news) — 관세청 UNIPASS 공지 큐레이션 피드 (30분 캐시).
 * "여기만 들어와도 공지가 다 있다" — 제목 클릭 시 본문 전문, 원문은 유니패스 게시판.
 */
import React, { useState, useEffect } from "react";
import ApiPortalLayout from "./ApiPortalLayout";
import { amendmentNoticeToNewsItem, fetchRecentAmendmentNotices } from "./amendmentNotice";
import AmendmentPdfViewerModal, { AmendmentPdfViewerButton } from "../audit/AmendmentPdfViewer";
import { customsNoticeOriginalUrl, mergeNewsItems } from "./newsFeed";

const BRAND_INK = "#1a1a1a";

const S = {
  page: { maxWidth: 880, color: BRAND_INK,
    fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  item: { border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10,
    background: "#fff", overflow: "hidden" },
  head: { display: "flex", alignItems: "baseline", gap: 10, padding: "12px 16px",
    cursor: "pointer", flexWrap: "wrap" },
  date: { fontSize: 12.5, color: "#9ca3af", fontFamily: "ui-monospace,monospace",
    flexShrink: 0 },
  badge: { fontSize: 11.5, fontWeight: 700, padding: "1px 8px", borderRadius: 10,
    background: "#f1f3f5", color: BRAND_INK, flexShrink: 0 },
  badgeLoginhs: { fontSize: 11.5, fontWeight: 700, padding: "1px 8px", borderRadius: 10,
    background: "#f1f3f5", color: BRAND_INK, flexShrink: 0 },
  title: { fontSize: 14.5, fontWeight: 600, color: BRAND_INK },
  body: { padding: "0 16px 14px", fontSize: 13.5, color: "#4b5563",
    whiteSpace: "pre-wrap", lineHeight: 1.75, borderTop: "1px dashed #e5e7eb",
    paddingTop: 12 },
  bodyToolbar: { display: "flex", justifyContent: "flex-end", marginBottom: 10 },
  originalLink: { display: "inline-flex", alignItems: "center", border: "1px solid #d1d5db",
    borderRadius: 6, padding: "4px 9px", background: "#fff", color: "#2563eb",
    fontSize: 12, fontWeight: 700, lineHeight: 1.5, textDecoration: "none", whiteSpace: "nowrap" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
  attachments: { marginTop: 12, paddingTop: 10, borderTop: "1px solid #e5e7eb" },
};

// loginHS 자체 공지 — 관세청 크롤 위에 노출(NewsPage + 랜딩 스트립 공용). 서비스 반영·소식은 여기에 추가/제거.
export const LOGINHS_NOTICES = [
  {
    id: "loginhs-2026-07-16-gosi",
    date: "2026-07-16",
    type: "loginHS 안내",
    title: "세관장확인고시 개정(7.16 시행) 반영 완료",
    content:
      "「관세법 제226조에 따른 세관장확인물품 및 확인방법 지정고시」 개정(관세청 고시 제2025-57호, 2026.7.16 시행)에 따른 HSK별 수출입 요건 변경을 세번상세 요건사항에 반영했습니다.\n\n체외진단의료기기법(신규) 등 관련 법령의 소관부처·연락처도 함께 갱신했습니다. 세번을 조회하시면 최신 요건이 표시됩니다.",
    _loginhs: true,
  },
];

export default function NewsPage({ onNavigate, embedded = false }) {
  const [data, setData] = useState(null);
  const [amendments, setAmendments] = useState([]);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null); // 펼친 항목 key
  const [attachmentState, setAttachmentState] = useState({});
  const [pdfEvidence, setPdfEvidence] = useState(null);

  useEffect(() => {
    fetchRecentAmendmentNotices(12)
      .then((items) => setAmendments(items.map(amendmentNoticeToNewsItem).filter(Boolean)))
      .catch(() => {});
    fetch("/api/onto/customs-notices")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then(setData)
      .catch(() => setErr("공지를 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요."));
  }, []);

  const customsItems = Array.isArray(data?.items)
    ? data.items.map((item) => ({ ...item, _customs: true }))
    : [];
  const items = mergeNewsItems(amendments, LOGINHS_NOTICES, customsItems);
  const toggleNotice = (item, key) => {
    const willOpen = open !== key;
    setOpen(willOpen ? key : null);
    const hasAttachments = item.has_attachments || Number(item.attachment_count) > 0;
    if (!willOpen || !hasAttachments || !item.id || attachmentState[item.id]) return;
    setAttachmentState((current) => ({
      ...current,
      [item.id]: { loading: true, items: [], error: null },
    }));
    fetch(`/api/onto/customs-notices/${encodeURIComponent(item.id)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((detail) => setAttachmentState((current) => ({
        ...current,
        [item.id]: {
          loading: false,
          items: Array.isArray(detail?.attachments) ? detail.attachments : [],
          error: null,
        },
      })))
      .catch(() => setAttachmentState((current) => ({
        ...current,
        [item.id]: { loading: false, items: [], error: "첨부파일 목록을 불러오지 못했습니다." },
      })));
  };

  return (
    <ApiPortalLayout active="news" onNavigate={onNavigate} embedded={embedded}>
      <div className="lhs-news-page" style={S.page}>
        <style>{`
          .lhs-news-page a { color: ${BRAND_INK}; text-decoration-color: #9ca3af; text-decoration-thickness: 1px; text-underline-offset: 3px; }
          .lhs-news-page a:hover { text-decoration-style: solid; }
        `}</style>
        <h1 style={S.h1}>새소식 — 관세청 공지</h1>
        <p style={S.p}>
          관세청 유니패스 공지사항을 모아 보여드립니다. 제목을 누르면 본문을 바로
          확인할 수 있습니다.
          {data && (
            <>
              {" "}전체 {Number(data.total_count).toLocaleString()}건은{" "}
              <a href={data.board_url} target="_blank" rel="noopener noreferrer">
                유니패스 공지 게시판 ↗
              </a>
              에서 보실 수 있습니다.
            </>
          )}
        </p>

        {err && <div style={{ marginTop: 14, fontSize: 13, color: "#b91c1c" }}>{err}</div>}
        {!err && !data && (
          <div style={{ marginTop: 14, fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>
        )}

        <div style={{ marginTop: 14 }}>
          {items.map((x, i) => {
            const itemKey = x.id || `${x.date}-${x.title}-${i}`;
            const isOpen = open === itemKey;
            const originalUrl = x._customs ? customsNoticeOriginalUrl(x.id) : "";
            return (
              <div key={itemKey} style={S.item}>
                <div style={S.head} onClick={() => toggleNotice(x, itemKey)}>
                  <span style={S.date}>{x.date}</span>
                  {x.type && (
                    <span style={x._loginhs ? S.badgeLoginhs : S.badge}>{x.type}</span>
                  )}
                  <span style={S.title}>{x.title}</span>
                  <span style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 12 }}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                </div>
                {isOpen && (
                  <div style={S.body}>
                    {originalUrl && (
                      <div style={S.bodyToolbar}>
                        <a
                          href={originalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={S.originalLink}
                          aria-label={`관세청 원문 확인: ${x.title}`}
                        >
                          관세청 원문 확인 ↗
                        </a>
                      </div>
                    )}
                    <div>
                      {x.content || "본문이 없습니다 — 원문은 유니패스 게시판을 확인하세요."}
                    </div>
                    {x._amendment && x.items?.length > 0 && (
                      <AmendmentItemList
                        items={x.items}
                        gosiNo={x.gosiNo}
                        onOpenPdf={(hsk, item) => setPdfEvidence({ hsk, item })}
                      />
                    )}
                    {x.org && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
                        관련 기관: {x.org}
                      </div>
                    )}
                    {x._amendment && x.url && (
                      <div style={{ marginTop: 10 }}>
                        <a href={x.url} target="_blank" rel="noopener noreferrer">
                          국가법령정보센터 원문 확인 ↗
                        </a>
                      </div>
                    )}
                    {(x.has_attachments || Number(x.attachment_count) > 0) && (
                      <NoticeAttachments state={attachmentState[x.id]} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {pdfEvidence && (
          <AmendmentPdfViewerModal
            hsk={pdfEvidence.hsk}
            item={pdfEvidence.item}
            onClose={() => setPdfEvidence(null)}
          />
        )}

        <div style={S.note}>
          출처: 관세청 유니패스 공지사항 (30분 간격 갱신). 공지에 등록된 첨부파일은
          본문을 펼쳐 바로 내려받을 수 있습니다. 전체 목록과 원문은{" "}
          <a href={data?.board_url} target="_blank" rel="noopener noreferrer">원문 게시판</a>
          에서 확인하세요.
        </div>
      </div>
    </ApiPortalLayout>
  );
}

function NoticeAttachments({ state }) {
  if (!state || state.loading) {
    return <div style={S.attachments}>첨부파일을 불러오는 중…</div>;
  }
  if (state.error) {
    return <div style={{ ...S.attachments, color: "#b91c1c" }}>{state.error}</div>;
  }
  if (state.items.length === 0) {
    return <div style={S.attachments}>등록된 첨부파일을 확인하지 못했습니다.</div>;
  }
  return (
    <div style={S.attachments}>
      <div style={{ color: BRAND_INK, fontSize: 13, fontWeight: 750, marginBottom: 6 }}>
        첨부파일 {state.items.length}개
      </div>
      {state.items.map((attachment) => (
        <div key={attachment.id} style={{ marginTop: 4 }}>
          <a href={attachment.download_url} download>
            {attachment.name}
          </a>
          {attachment.size && (
            <span style={{ marginLeft: 7, color: "#9ca3af", fontSize: 12 }}>
              {attachment.size}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function AmendmentItemList({ items, gosiNo, onOpenPdf }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ marginBottom: 7, color: BRAND_INK, fontSize: 13, fontWeight: 750 }}>
        실제 반영 품목 {items.length}건
      </div>
      <div style={{ borderTop: "1px solid #e5e7eb" }}>
        {items.map((item, index) => {
          const viewerItem = {
            ...item,
            gosiNo,
            sourceLandingUrl: item.sourceUrl,
            sourceUrl: item.sourcePdfUrl,
          };
          const evidenceHsk = String(item.evidenceHsk || "").replace(/[^0-9]/g, "");
          return (
            <div
              key={item.id || `${item.seqNo}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "28px minmax(0, 1fr) auto",
                gap: 9,
                alignItems: "start",
                padding: "10px 0",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <span style={{ color: "#9ca3af", fontFamily: "ui-monospace,monospace", fontSize: 12 }}>
                {index + 1}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: BRAND_INK, fontSize: 13.5, fontWeight: 650, lineHeight: 1.45 }}>
                  {noticeItemTitle(item.title) || `변경 품목 연번 ${item.seqNo || index + 1}`}
                </div>
                <AmendmentPairLines item={item} />
              </div>
              {evidenceHsk.length === 10 && item.sourcePage && (
                <AmendmentPdfViewerButton
                  hsk={evidenceHsk}
                  item={viewerItem}
                  onOpen={() => onOpenPdf(evidenceHsk, viewerItem)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AmendmentPairLines({ item }) {
  if (item.reviewNeeded) {
    return (
      <div style={{ marginTop: 3, color: "#6b7280", fontSize: 12.5 }}>
        {item.beforeCodes?.length > 0 && (
          <><code style={codeStyle}>{item.beforeCodes.map(formatHsk).join(" · ")}</code>{" · "}</>
        )}
        변경 세번 원문 대조 중 — 확정 표시하지 않음
      </div>
    );
  }
  const pairs = Array.isArray(item.pairs) ? item.pairs : [];
  if (pairs.length > 0) {
    return pairs.map((pair, index) => (
      <div key={index} style={{ marginTop: 3, color: "#4b5563", fontSize: 12.5 }}>
        <code style={codeStyle}>{(pair.before || []).map(formatHsk).join(" · ")}</code>
        <span aria-hidden="true"> → </span>
        <code style={codeStyle}>{formatHsk(pair.after)}</code>
      </div>
    ));
  }
  const before = item.beforeCodes || [];
  const after = item.afterCodes || [];
  if (before.length === 0 && after.length === 0) return null;
  return (
    <div style={{ marginTop: 3, color: "#4b5563", fontSize: 12.5 }}>
      <code style={codeStyle}>{before.map(formatHsk).join(" · ") || "—"}</code>
      <span aria-hidden="true"> → </span>
      <code style={codeStyle}>{after.map(formatHsk).join(" · ") || "—"}</code>
    </div>
  );
}

const codeStyle = {
  color: BRAND_INK,
  fontFamily: "'JetBrains Mono',ui-monospace,monospace",
  fontSize: 12.5,
  fontWeight: 650,
};

function formatHsk(value) {
  const digits = String(value || "").replace(/[^0-9]/g, "");
  return /^\d{10}$/.test(digits)
    ? `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6)}`
    : String(value || "—");
}

function noticeItemTitle(value) {
  return String(value || "")
    .replace(/\s*에\s*대한\s*품목분류\s*변경고시.*$/u, "")
    .trim();
}
